// Un mapa de Leaflet atado a un div: se crea cuando la librería terminó de
// cargar (hooks/useLeaflet) y se destruye al salir de la pantalla (si no,
// cada visita dejaba un mapa vivo con sus puntos y sus eventos llamando a
// un componente ya desmontado).
//
// `setup` corre una sola vez, con el mapa recién creado: ahí se arman los
// grupos de capas y los eventos del mapa; lo que devuelve viaja junto al mapa.
import { RefObject, useEffect, useRef, useState } from "react";
import { useLeaflet } from "./useLeaflet";
import { BaseMapOptions, createBaseMap } from "../lib/baseMap";
import { leafletGlobal, LeafletLite, LMap } from "../lib/leafletTypes";

export type LeafletMap<T> = { L: LeafletLite; map: LMap; extra: T };

export function useLeafletMap<T>(ref: RefObject<HTMLDivElement | null>, options: BaseMapOptions, setup: (L: LeafletLite, map: LMap) => T): { ready: boolean; leafletMap: LeafletMap<T> | null } {
  const ready = useLeaflet();
  const [leafletMap, setLeafletMap] = useState<LeafletMap<T> | null>(null);
  // Se leen al crear el mapa, una vez: cambiarlos después no lo recrea.
  const init = useRef({ options, setup });

  useEffect(() => {
    const L = leafletGlobal();
    if (!ready || !L || !ref.current) return;
    const map = createBaseMap(L, ref.current, init.current.options);
    setLeafletMap({ L, map, extra: init.current.setup(L, map) });
    return () => { map.off(); map.remove(); setLeafletMap(null); };
  }, [ready, ref]);

  return { ready, leafletMap };
}
